import type { StandardSliderNavigation } from '~/components/StandardSlider';
import type { Property } from '~/core/entities/property';
import type {
	CarouselId,
	CarouselItemClicked,
	CarouselItemNavigated,
	SectionName
} from '~/types/mixpanel';

import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';

export interface CarouselTrackingContext {
	carouselId: CarouselId;
	currentPage: string;
	sectionName: SectionName;
}

export function buildCarouselItemClickedParams(
	{ carouselId, currentPage, sectionName }: CarouselTrackingContext,
	property: Pick<Property, 'commercialId'>,
	index: number
): CarouselItemClicked {
	return {
		[MixpanelProps.CAROUSEL_ID]: carouselId,
		[MixpanelProps.ITEM_POSITION]: index + 1,
		[MixpanelProps.ITEM_TYPE]: 'property',
		[MixpanelProps.ITEM_ID]: property.commercialId,
		[MixpanelProps.CURRENT_PAGE]: currentPage,
		[MixpanelProps.SECTION_NAME]: sectionName
	};
}

export function buildCarouselItemNavigatedParams(
	{ carouselId, currentPage, sectionName }: CarouselTrackingContext,
	{ direction, fromIndex, toIndex }: StandardSliderNavigation
): CarouselItemNavigated {
	return {
		[MixpanelProps.CAROUSEL_ID]: carouselId,
		[MixpanelProps.DIRECTION]: direction,
		[MixpanelProps.FROM_POSITION]: fromIndex + 1,
		[MixpanelProps.TO_POSITION]: toIndex + 1,
		[MixpanelProps.CURRENT_PAGE]: currentPage,
		[MixpanelProps.SECTION_NAME]: sectionName
	};
}

/**
 * Eventos `carousel_item_*` de um carrossel. O slider trabalha com índices
 * 0-based; o Mixpanel recebe posições 1-based.
 */
export function useCarouselTracking(
	carouselId: CarouselId,
	sectionName: SectionName
) {
	const route = useRoute();
	const { trackCarouselItemClicked, trackCarouselItemNavigated } = useEvents();

	function getContext(): CarouselTrackingContext {
		return { carouselId, currentPage: route.fullPath, sectionName };
	}

	function trackItemClicked(
		property: Pick<Property, 'commercialId'>,
		index: number
	): void {
		trackCarouselItemClicked(
			buildCarouselItemClickedParams(getContext(), property, index)
		);
	}

	function trackNavigated(navigation: StandardSliderNavigation): void {
		trackCarouselItemNavigated(
			buildCarouselItemNavigatedParams(getContext(), navigation)
		);
	}

	return { trackItemClicked, trackNavigated };
}
